import type { Manifest } from '@clawharbor/manifest';
import { contrastSafeOverrides, googleFontHref, tokensToCss } from '@clawharbor/render-core';

/**
 * The crawled brand, scoped to one card.
 *
 * `tokensToCss` is the same emitter that paints the hosted WebMCP page and the
 * MCP Apps widget. Calling it here rather than reimplementing a preview means
 * the swatches on the result screen cannot drift from what actually ships — if
 * the card looks right, the surfaces look right, because it is one function.
 *
 * It is scoped to `.tenant` rather than `:root` on purpose. A harvested palette
 * applied to the whole console would repaint the tool doing the harvesting, and
 * a #051851 canvas behind a review screen is how you get an operator approving
 * something they could not read. Two elements opt in: the masthead that opens
 * the result screen, and the specimen card. Everything the operator has to READ
 * to make the publish decision stays on the console's own ground.
 *
 * `contrastSafeOverrides` is appended for the same reason the surfaces apply it:
 * a palette lifted off someone else's CSS can pair text and ground at 2:1, and
 * a masthead nobody can read is not a demonstration of anything. The correction
 * is stated on screen rather than performed silently.
 */

const TOKEN_STYLE_ID = 'ch-tenant-tokens';
const FONT_LINK_ID = 'ch-spec-font';
const FONT_PRECONNECT_ID = 'ch-spec-font-pre';

function upsert<T extends HTMLElement>(id: string, tag: string): T {
  const found = document.getElementById(id);
  if (found) return found as T;
  const el = document.createElement(tag);
  el.id = id;
  document.head.appendChild(el);
  return el as T;
}

export function applyTenantTokens(manifest: Manifest): void {
  const style = upsert<HTMLStyleElement>(TOKEN_STYLE_ID, 'style');
  const scope = '.tenant';
  const css = `${tokensToCss(manifest.brand, scope)}
${contrastSafeOverrides(manifest.brand, scope)}`;
  if (style.textContent !== css) style.textContent = css;

  /**
   * Only a declared SUBSTITUTE is ever fetched. `googleFontHref` returns null
   * unless a family sets `substitute.source === 'google'`, so CineStar's
   * proprietary self-hosted DIN Pro is never requested and never rehosted —
   * the specimen shows Barlow, which is what the surfaces would really serve.
   */
  const href = googleFontHref(manifest.brand);
  if (!href) return;
  const pre = upsert<HTMLLinkElement>(FONT_PRECONNECT_ID, 'link');
  pre.rel = 'preconnect';
  pre.href = 'https://fonts.gstatic.com/';
  pre.crossOrigin = 'anonymous';
  const link = upsert<HTMLLinkElement>(FONT_LINK_ID, 'link');
  link.rel = 'stylesheet';
  if (link.href !== href) link.href = href;
}

export function clearTenantTokens(): void {
  for (const id of [TOKEN_STYLE_ID, FONT_LINK_ID, FONT_PRECONNECT_ID]) {
    document.getElementById(id)?.remove();
  }
}

/** Wordmark text, never a logo file — the same rule apps/web enforces. */
export function wordmarkText(manifest: Manifest): string {
  const wm = manifest.identity.wordmark;
  if (!wm) return manifest.identity.displayName;
  if (wm.case === 'upper') return wm.text.toUpperCase();
  if (wm.case === 'lower') return wm.text.toLowerCase();
  return wm.text;
}
